import { Card, Form, FormButtonRow, IconButton } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { Profession } from '@common/enums';
import type { IEmployment } from '@frontend/components/interfaces/employment';
import type { IPractitioner } from '@frontend/components/interfaces/practitioner';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums';
import { formatString } from '@common/utils/formatString';
import { padStartBigCode } from '@common/utils/padStartBigCode';
import { registrationCodesFrontendSchema } from '@common/validation/practitioner';
import {
    identifierDeclarationOrSupervisorRequired,
    identifierDeclarationRequired,
} from '@common/validation/utils/accountFieldsRequirementsByProfession';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { FormParagraphFormGroupField } from '@frontend/components/fields/FormParagraphField';
import { CancelButton } from '@frontend/components/userDataManagement/CancelButton';
import { IdentifierDeclaration } from '@frontend/components/userDataManagement/IdentifierDeclaration';
import { InputTextField } from '@frontend/components/userDataManagement/InputText';
import { SaveButton } from '@frontend/components/userDataManagement/SaveButton';
import { getSelectedLanguage } from '@frontend/translate';
import {
    emptyValidationErrors,
    hasOneAuthorization,
    updateValidationErrorsForOne,
    useAbortableFetch,
    validate,
} from '@frontend/utils';

interface IProfileRegistrationCodesProps {
    practitionerId: string;
}

export function ProfileRegistrationCodes(props: Readonly<IProfileRegistrationCodesProps>) {
    const [practitioner, setPractitioner] = useState<IPractitioner>();
    const [originalPractitioner, setOriginalPractitioner] = useState<IPractitioner>();
    const [practitionerEmployments, setPractitionerEmployments] = useState<IEmployment[]>();
    const [isEditing, setIsEditing] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [disableSaveButton, setDisableSaveButton] = useState<boolean>(false);
    const [isRequiredIdentifierDeclaration, setIsRequiredIdentifierDeclaration] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const { id: contextPractitionerId } = useSelector((state: IReduxState) => state.authentication);
    const translate = getSelectedLanguage();

    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        loadPractitioner();
        loadEmployments();
    }, [props.practitionerId]);

    useEffect(() => {
        const canClearAgbCode = canClearIdentifierDeclaration();
        setIsRequiredIdentifierDeclaration(canClearAgbCode);
        setDisableSaveButton(canClearAgbCode && !practitioner?.identifierDeclaration);
    }, [practitioner, practitionerEmployments]);

    if (!practitioner || !canView()) {
        return null;
    }

    return (
        <FormContextProvider
            value={{
                errors: errors,
                item: practitioner,
                onChange: (key, value) => handleChange(key, value),
                onDoValidate: (key) => validateOne(key),
                placeholderGroupName: 'profileRegistrationCodes',
            }}
        >
            <Card>
                <Card.Transition type={canEdit && isEditing ? 'edit' : 'read'} />
                <Card.Header
                    action={
                        canEdit && !isEditing ? (
                            <IconButton
                                icon="edit-light"
                                labelText="edit"
                                onClick={() => setIsEditing(true)}
                            />
                        ) : undefined
                    }
                    title={translate.practitioner.registrations}
                />
                <Card.Body>
                    <Form submitOnEnter={false}>
                        {renderIdentifierDeclaration()}

                        {renderTextInput(
                            'identifierRegistrationBig',
                            'identifierDeclarationOrRegistration.identifierRegistrationBig',
                            formatString(translate.personalSettings.addIdentifier, [
                                translate.identifierDeclarationOrRegistration.identifierRegistrationBig,
                            ])
                        )}

                        {renderTextInput(
                            'identifierRegistrationKp',
                            'identifierDeclarationOrRegistration.identifierRegistrationKp',
                            formatString(translate.personalSettings.addIdentifier, [
                                translate.identifierDeclarationOrRegistration.identifierRegistrationKp,
                            ])
                        )}

                        {isEditing && (
                            <FormButtonRow>
                                <CancelButton
                                    disabled={isSaving}
                                    onCancel={async () => handleCancel()}
                                />
                                <SaveButton
                                    disabled={isSaving || disableSaveButton}
                                    onSave={async () => await handleSubmit()}
                                />
                            </FormButtonRow>
                        )}
                    </Form>
                </Card.Body>
            </Card>
        </FormContextProvider>
    );

    function renderIdentifierDeclaration() {
        return (
            <IdentifierDeclaration
                dontValidateOnBlur
                errors={errors}
                isRequired={isEditing && isRequiredIdentifierDeclaration}
                onChange={async (key, value) => handleChange(key, value as unknown as IPractitioner)}
                onValidateOne={async (key) => validateOne(key)}
                readOnly={!isEditing}
                showHasDeclarableQualificationField={true}
                value={practitioner as IPractitioner}
            />
        );
    }

    function renderTextInput(
        fieldKey: string,
        label: string,
        placeholder: string,
        requiredForProfessions?: Profession[]
    ) {
        const isRequired = practitioner?.professions?.some((profession) =>
            requiredForProfessions?.includes(profession)
        );

        if (isEditing) {
            return (
                <InputTextField
                    dontValidateOnBlur
                    error={errors.get(fieldKey)}
                    fieldKey={fieldKey}
                    hasErrors={errors.has(fieldKey)}
                    isRequired={isEditing && isRequired}
                    label={label}
                    nullable
                    placeholder={placeholder}
                />
            );
        }

        return (
            <FormParagraphFormGroupField
                fieldKey={fieldKey}
                label={label}
            />
        );
    }

    function canView() {
        return (
            hasOneAuthorization([ManagerAuthorization.adminsRead, ManagerAuthorization.practitionerRead]) ||
            props.practitionerId === contextPractitionerId
        );
    }

    async function loadPractitioner() {
        const result = await abortableFetch.fetchJson(`/api/practitioners/${props.practitionerId}`, 'GET');
        setPractitioner(result.json);
        setOriginalPractitioner(result.json);
    }

    function canClearIdentifierDeclaration() {
        const becauseIdentifierDeclarationRequired = practitioner?.professions?.some((profession) =>
            identifierDeclarationRequired.includes(profession)
        );
        const becauseIdentifierDeclarationOrSupervisorRequired = practitionerEmployments?.some(
            (employment) =>
                employment.profession &&
                identifierDeclarationOrSupervisorRequired.includes(employment.profession) &&
                !employment.identifierDeclarationPractitionerId
        );

        return Boolean(becauseIdentifierDeclarationRequired || becauseIdentifierDeclarationOrSupervisorRequired);
    }

    function handleCancel() {
        setPractitioner(originalPractitioner);
        setIsEditing(false);
        setErrors(emptyValidationErrors);
    }

    function handleChange<K extends keyof IPractitioner>(name: K, value: IPractitioner) {
        setPractitioner({ ...practitioner, [name]: value } as IPractitioner);
    }

    function validateOne(name: keyof IPractitioner) {
        setErrors(updateValidationErrorsForOne(registrationCodesFrontendSchema, practitioner, errors, name));
    }

    async function loadEmployments() {
        const result = await abortableFetch.fetchJson(
            `/api/user/employments?practitionerId=${props.practitionerId}`,
            'GET'
        );
        const employments: IEmployment[] = result.json.items;

        const isAuthorized = employments.some((e) => e.organization?.isKsyos)
            ? hasOneAuthorization(ManagerAuthorization.adminsAddEdit)
            : hasOneAuthorization([
                  ManagerAuthorization.employmentSettingsEdit,
                  ManagerAuthorization.practitionerAddEdit,
              ]);

        setCanEdit(isAuthorized);
        setPractitionerEmployments(employments);
    }

    async function handleSubmit() {
        if (practitioner) {
            setIsSaving(true);

            const validationResult = validate(registrationCodesFrontendSchema, practitioner);

            if (validationResult.hasErrors) {
                setErrors(validationResult.errors);
            } else {
                const paddedIdentifierRegistrationBig = padStartBigCode(
                    validationResult.item.identifierRegistrationBig
                );
                validationResult.item.identifierRegistrationBig = paddedIdentifierRegistrationBig ?? null;

                const { professions, ...filteredRegistrationCode } = { ...validationResult.item };

                const url = `/api/practitioners/${practitioner.id}/registrationCodes`;
                const result = await abortableFetch.fetchJson(url, 'POST', filteredRegistrationCode, [
                    ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS,
                    ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS,
                    ValidationErrorCode.IDENTIFIER_REGISTRATION_KP_EXISTS,
                ]);

                if (result.errorCode) {
                    if (result.errorCode === ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS) {
                        setErrors(errors.set('identifierDeclaration', result.errorCode));
                    }

                    if (result.errorCode === ValidationErrorCode.IDENTIFIER_REGISTRATION_BIG_EXISTS) {
                        setErrors(errors.set('identifierRegistrationBig', result.errorCode));
                    }

                    if (result.errorCode === ValidationErrorCode.IDENTIFIER_REGISTRATION_KP_EXISTS) {
                        setErrors(errors.set('identifierRegistrationKp', result.errorCode));
                    }
                } else {
                    const practitionerUpdate = { ...practitioner, ...validationResult.item };

                    setOriginalPractitioner(practitionerUpdate);
                    setPractitioner(practitionerUpdate);
                    setErrors(emptyValidationErrors);
                    setIsEditing(false);
                }
            }

            setIsSaving(false);
        }
    }
}
